import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { clearTeacherSession, acBase, Spinner, fmtDateTime, fmtDate, fmtTime, buildZoomLink, CATEGORY_COLORS, WAIcon, ADMIN_WA } from './academyUtils';
import ProgressDashboard from '../ProgressDashboard';
import { hashPassword } from '../../utils/auth';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const GROUP_SIZES = [1,2,3,4,8,10];
const SLOT_TYPES = ['regular','trial','group'];

function isoToInputDate(iso) {
  const d = new Date(iso);
  return d.toISOString().slice(0,10);
}
function isoToInputTime(iso) {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

const SCORE_OPTS = [0,0.5,1,1.5,2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5,8,8.5,9];

function calcOverall(f,l,g,p) {
  const nums = [f,l,g,p].map(Number).filter(n => !isNaN(n) && n > 0);
  if (nums.length < 4) return null;
  const avg = nums.reduce((a,b)=>a+b,0)/4;
  return Math.round(avg*2)/2;
}

const CRITERIA = [
  { key:'fluency',       label:'Fluency & Coherence' },
  { key:'lexical',       label:'Lexical Resource' },
  { key:'grammar',       label:'Grammatical Range & Accuracy' },
  { key:'pronunciation', label:'Pronunciation' },
];

export default function TeacherDashboard({ teacher, onLogout, onBack }) {
  const [tab,       setTab]       = useState('schedule');
  const [month,     setMonth]     = useState(new Date().getMonth());
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [slots,     setSlots]     = useState([]);
  const [bookings,  setBookings]  = useState([]);
  const [categories,setCategories]= useState([]);
  const [loading,   setLoading]   = useState(false);
  const [slotModal, setSlotModal] = useState(null); // null | { date, slot? }
  const [profile,   setProfile]   = useState(teacher);
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState('');

  // Speaking exams
  const [speakingExams,  setSpeakingExams]  = useState([]);
  const [reportPanel,    setReportPanel]    = useState(null); // exam id
  const [scores,         setScores]         = useState({ fluency:'', lexical:'', grammar:'', pronunciation:'' });
  const [reportComments, setReportComments] = useState('');
  const [sendingReport,  setSendingReport]  = useState(false);

  // Slot form state
  const [slotStart,  setSlotStart]  = useState('09:00');
  const [slotEnd,    setSlotEnd]    = useState('10:00');
  const [slotMax,    setSlotMax]    = useState(1);
  const [slotType,   setSlotType]   = useState('regular');
  const [recurring,  setRecurring]  = useState(false);
  const [recurWeeks, setRecurWeeks] = useState(4);
  const [slotErr,    setSlotErr]    = useState('');
  const [slotLoading,setSlotLoading]= useState(false);

  // Profile form
  const [editName,     setEditName]     = useState(teacher.name);
  const [editWA,       setEditWA]       = useState(teacher.whatsapp || '');
  const [editBio,      setEditBio]      = useState(teacher.bio || '');
  const [editNat,      setEditNat]      = useState(teacher.nationality || '');
  const [editYears,    setEditYears]    = useState(teacher.years_experience || 0);
  const [editPhoto,    setEditPhoto]    = useState(teacher.photo_url || '');
  const [editVoice,    setEditVoice]    = useState(teacher.voice_intro_url || '');
  const [editSamples,  setEditSamples]  = useState(Array.isArray(teacher.sample_work) ? teacher.sample_work : []);
  const [editNewPwd,   setEditNewPwd]   = useState('');
  const [confNewPwd,   setConfNewPwd]   = useState('');

  const fetchSlots = useCallback(async () => {
    const first = new Date(year, month, 1).toISOString();
    const last  = new Date(year, month+1, 0, 23, 59).toISOString();
    const { data } = await supabase.from('time_slots')
      .select('*').eq('teacher_id', teacher.id)
      .gte('start_time', first).lte('start_time', last)
      .order('start_time');
    setSlots(data || []);
  }, [teacher.id, month, year]);

  const fetchBookings = useCallback(async () => {
    const { data } = await supabase.from('bookings')
      .select('*, students(name,whatsapp), time_slots(start_time,end_time)')
      .eq('teacher_id', teacher.id)
      .order('created_at', { ascending: false })
      .limit(50);
    setBookings(data || []);
  }, [teacher.id]);

  const fetchCategories = useCallback(async () => {
    const { data } = await supabase.from('teacher_categories_map')
      .select('teacher_categories(name,icon,color,bg_color)')
      .eq('teacher_id', teacher.id);
    setCategories(data?.map(d => d.teacher_categories).filter(Boolean) || []);
  }, [teacher.id]);

  const [assignedStudents, setAssignedStudents] = useState([]);
  const fetchAssignedStudents = useCallback(async () => {
    const { data } = await supabase.from('subscriptions')
      .select('id, subscription_type, students(id,name)')
      .eq('teacher_id', teacher.id)
      .eq('status', 'active');
    // Deduplicate by student id
    const seen = new Set();
    const unique = (data||[]).filter(x => {
      if (!x.students?.id || seen.has(x.students.id)) return false;
      seen.add(x.students.id); return true;
    });
    setAssignedStudents(unique);
  }, [teacher.id]);

  const fetchSpeakingExams = useCallback(async () => {
    try {
      const { data } = await supabase.from('speaking_exams')
        .select('*').eq('examiner_id', teacher.id)
        .order('scheduled_at', { ascending: true });
      setSpeakingExams(data || []);
    } catch { setSpeakingExams([]); }
  }, [teacher.id]);

  // Earnings
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [hoursThisMonth, setHoursThisMonth] = useState([]);
  const [payConfig,      setPayConfig]      = useState(null);

  const fetchEarnings = useCallback(async () => {
    try {
      const [{ data: pr }, { data: cfg }] = await Promise.all([
        supabase.from('teacher_payroll').select('*').eq('teacher_id', teacher.id).order('year,month', { ascending: false }).limit(7),
        supabase.from('teacher_payment_config').select('*').eq('teacher_id', teacher.id).single(),
      ]);
      setPayrollRecords(pr || []);
      setPayConfig(cfg);

      const now = new Date();
      const start = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
      const end   = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().slice(0,10);
      const { data: hrs } = await supabase.from('teacher_hours_log')
        .select('*, students(name)').eq('teacher_id', teacher.id)
        .gte('session_date', start).lte('session_date', end)
        .order('session_date', { ascending: false });
      setHoursThisMonth(hrs || []);
    } catch { /* table may not exist yet */ }
  }, [teacher.id]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);
  useEffect(() => { if (tab==='bookings') { fetchBookings(); fetchAssignedStudents(); } }, [tab, fetchBookings, fetchAssignedStudents]);
  useEffect(() => { if (tab==='speaking') fetchSpeakingExams(); }, [tab, fetchSpeakingExams]);
  useEffect(() => { if (tab==='earnings') fetchEarnings(); }, [tab, fetchEarnings]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // ── Calendar helpers ────────────────────────────────────────
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth     = new Date(year, month+1, 0).getDate();

  function slotsForDay(day) {
    const prefix = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return slots.filter(s => s.start_time?.startsWith(prefix));
  }

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y=>y-1); }
    else setMonth(m=>m-1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y=>y+1); }
    else setMonth(m=>m+1);
  }

  function openAddSlot(day) {
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    setSlotStart('09:00'); setSlotEnd('10:00'); setSlotMax(1); setSlotType('regular');
    setRecurring(false); setRecurWeeks(4); setSlotErr('');
    setSlotModal({ date: dateStr, slot: null });
  }

  function openEditSlot(slot) {
    setSlotStart(isoToInputTime(slot.start_time));
    setSlotEnd(isoToInputTime(slot.end_time));
    setSlotMax(slot.max_students || 1);
    setSlotType(slot.slot_type || 'regular');
    setRecurring(false); setRecurWeeks(4); setSlotErr('');
    setSlotModal({ date: isoToInputDate(slot.start_time), slot });
  }

  async function saveSlot() {
    setSlotErr('');
    if (!slotStart || !slotEnd) { setSlotErr('Please set start and end times.'); return; }
    const [sh,sm] = slotStart.split(':').map(Number);
    const [eh,em] = slotEnd.split(':').map(Number);
    if (eh * 60 + em <= sh * 60 + sm) { setSlotErr('End time must be after start time.'); return; }
    setSlotLoading(true);

    const dates = [slotModal.date];
    if (recurring && !slotModal.slot) {
      const base = new Date(slotModal.date);
      for (let w = 1; w < recurWeeks; w++) {
        const d = new Date(base); d.setDate(d.getDate() + w * 7);
        dates.push(d.toISOString().slice(0,10));
      }
    }

    try {
      if (slotModal.slot) {
        // Edit existing
        const startISO = `${slotModal.date}T${slotStart}:00`;
        const endISO   = `${slotModal.date}T${slotEnd}:00`;
        const mins     = (eh*60+em) - (sh*60+sm);
        await supabase.from('time_slots').update({
          start_time: startISO, end_time: endISO,
          duration_minutes: mins, max_students: slotMax, slot_type: slotType,
        }).eq('id', slotModal.slot.id);
      } else {
        const rows = dates.map(date => ({
          teacher_id: teacher.id,
          start_time: `${date}T${slotStart}:00`,
          end_time:   `${date}T${slotEnd}:00`,
          duration_minutes: (eh*60+em)-(sh*60+sm),
          max_students: slotMax, slot_type: slotType,
          is_recurring: recurring && dates.length > 1,
          status: 'available',
        }));
        await supabase.from('time_slots').insert(rows);
      }
      setSlotModal(null);
      fetchSlots();
    } catch (e) {
      setSlotErr('Could not save slot. Please try again.');
    } finally { setSlotLoading(false); }
  }

  async function deleteSlot(id) {
    if (!window.confirm('Delete this time slot?')) return;
    await supabase.from('time_slots').delete().eq('id', id);
    setSlotModal(null);
    fetchSlots();
  }

  async function saveProfile() {
    setSaving(true); setSaveMsg('');
    const upd = {
      name: editName.trim(), whatsapp: editWA.trim(), bio: editBio.trim(),
      nationality: editNat.trim(), years_experience: Number(editYears) || 0,
      photo_url: editPhoto.trim(), voice_intro_url: editVoice.trim(),
      sample_work: editSamples,
    };
    if (editNewPwd) {
      if (editNewPwd.length < 8) { setSaveMsg('Password must be at least 8 characters.'); setSaving(false); return; }
      if (editNewPwd !== confNewPwd) { setSaveMsg('Passwords do not match.'); setSaving(false); return; }
      upd.password_hash = await hashPassword(editNewPwd);
    }
    const { data, error } = await supabase.from('teachers').update(upd).eq('id', teacher.id).select().single();
    setSaving(false);
    if (error) { setSaveMsg('Could not save. Please try again.'); return; }
    setProfile(data);
    setSaveMsg('✓ Profile saved successfully!');
    setEditNewPwd(''); setConfNewPwd('');
  }

  const slotColor = { regular:'#1a5fa8', trial:'#b8620a', group:'#1a7a4a' };
  const today = new Date(); const todayStr = today.toISOString().slice(0,10);

  // First-time setup banner
  const SETUP_KEY = `ca_teacher_setup_dismissed_${teacher.id}`;
  const [setupDismissed, setSetupDismissed] = React.useState(() => !!localStorage.getItem(SETUP_KEY));
  const showSetupBanner = !setupDismissed && slots.length === 0 && !loading;

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      {/* Top nav */}
      <div style={{ background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ background:'rgba(255,255,255,0.12)', borderRadius:8, padding:'4px 8px', display:'flex', alignItems:'center', flexShrink:0 }}>
            <img src="/logo.png" alt="IELTS Compass" style={{ height:30, objectFit:'contain' }}/>
          </div>
          <div>
            <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'.14em', textTransform:'uppercase' }}>Teacher Portal</div>
            <div style={{ fontWeight:700, fontSize:16, color:'#fff' }}>{profile.name}</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          {categories.map(c => (
            <span key={c.name} style={{ fontSize:11, fontWeight:600, color:c.color, background:`${c.color}22`, border:`1px solid ${c.color}44`, borderRadius:100, padding:'2px 9px' }}>{c.icon} {c.name}</span>
          ))}
          <button onClick={() => (onBack || onLogout)()} style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', color:'rgba(255,255,255,0.7)', padding:'6px 12px', borderRadius:7, cursor:'pointer', fontSize:11 }}>← Main Site</button>
          <button onClick={onLogout} style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', color:'#fff', padding:'6px 12px', borderRadius:7, cursor:'pointer', fontSize:12 }}>Sign Out</button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background:'#fff', borderBottom:'1px solid #dde3ed', display:'flex', padding:'0 20px', overflowX:'auto' }}>
        {(() => {
          const isIELTS = categories.some(c => c.name === 'IELTS' || c.name === 'IELTS Trainer');
          const tabs = [['schedule','📅 My Schedule'],['bookings','📋 My Bookings'],['earnings','💰 My Earnings']];
          if (isIELTS) tabs.push(['students-progress','📈 Students Progress'],['speaking','🎯 Speaking Exams']);
          tabs.push(['profile','👤 My Profile']);
          return tabs.map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} style={{ border:'none', borderBottom:`2px solid ${tab===k?'#b8860b':'transparent'}`, background:'none', color:tab===k?'#b8860b':'#5a6a88', padding:'13px 18px', fontSize:13, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap' }}>{l}</button>
          ));
        })()}
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'20px 16px' }}>

        {/* First-time setup banner */}
        {showSetupBanner && (
          <div style={{ background:'linear-gradient(135deg,#b8860b,#d4a017)', borderRadius:12, padding:'16px 20px', marginBottom:20, display:'flex', alignItems:'center', gap:16, flexWrap:'wrap', boxShadow:'0 3px 14px rgba(184,134,11,0.3)' }}>
            <div style={{ fontSize:28, flexShrink:0 }}>👋</div>
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ fontWeight:700, fontSize:14.5, color:'#fff', marginBottom:3 }}>Welcome! Set your available times so students can book lessons with you.</div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.85)', lineHeight:1.55 }}>Go to My Schedule → click any day → add your available time slots.</div>
            </div>
            <button onClick={() => { setTab('schedule'); setSetupDismissed(true); localStorage.setItem(SETUP_KEY,'1'); }}
              style={{ background:'#fff', color:'#b8860b', border:'none', padding:'9px 18px', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap' }}>
              Set My Schedule →
            </button>
            <button onClick={() => { setSetupDismissed(true); localStorage.setItem(SETUP_KEY,'1'); }}
              style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', width:28, height:28, borderRadius:'50%', cursor:'pointer', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>×</button>
          </div>
        )}

        {/* ── MY SCHEDULE ── */}
        {tab === 'schedule' && (
          <div>
            {/* Month nav */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <button onClick={prevMonth} style={{ ...acBase.btnOut, padding:'6px 14px' }}>‹</button>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a' }}>{MONTHS[month]} {year}</div>
              <button onClick={nextMonth} style={{ ...acBase.btnOut, padding:'6px 14px' }}>›</button>
            </div>

            {/* Day headers */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, marginBottom:4 }}>
              {DAYS.map(d=>(
                <div key={d} style={{ textAlign:'center', fontSize:11, fontFamily:"'JetBrains Mono',monospace", color:'#9aa3b8', padding:'6px 0' }}>{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4 }}>
              {Array.from({ length: firstDayOfMonth }).map((_,i) => <div key={`e${i}`}/>)}
              {Array.from({ length: daysInMonth }).map((_,i) => {
                const day = i+1;
                const daySlots = slotsForDay(day);
                const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const isToday = dateStr === todayStr;
                const isPast  = dateStr < todayStr;
                return (
                  <div key={day} onClick={()=>!isPast&&openAddSlot(day)}
                    style={{ minHeight:72, background: isToday?'#fffaf0':isPast?'#f7f9fc':'#fff', border:`1px solid ${isToday?'#b8860b':'#dde3ed'}`, borderRadius:8, padding:'6px 4px', cursor:isPast?'default':'pointer', transition:'box-shadow .15s' }}
                    onMouseEnter={e=>{ if(!isPast) e.currentTarget.style.boxShadow='0 2px 8px rgba(26,42,74,0.12)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.boxShadow='none'; }}>
                    <div style={{ fontSize:11, fontWeight:isToday?700:400, color:isToday?'#b8860b':'#5a6a88', marginBottom:3 }}>{day}</div>
                    {daySlots.map(s => (
                      <div key={s.id} onClick={e=>{e.stopPropagation();openEditSlot(s);}}
                        style={{ fontSize:10, background:`${slotColor[s.slot_type]||'#1a5fa8'}18`, color:slotColor[s.slot_type]||'#1a5fa8', border:`1px solid ${slotColor[s.slot_type]||'#1a5fa8'}44`, borderRadius:4, padding:'2px 4px', marginBottom:2, cursor:'pointer', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
                        {fmtTime(s.start_time)} {s.slot_type!=='regular'?`(${s.slot_type})`:''}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop:12, fontSize:12, color:'#9aa3b8', display:'flex', gap:16, flexWrap:'wrap' }}>
              {Object.entries(slotColor).map(([k,v])=>(
                <span key={k} style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ width:10, height:10, borderRadius:2, background:`${v}30`, border:`1px solid ${v}66`, display:'inline-block' }}/>
                  {k.charAt(0).toUpperCase()+k.slice(1)}
                </span>
              ))}
              <span style={{ marginLeft:'auto', color:'#5a6a88' }}>Click any future date to add a slot · Click a slot to edit</span>
            </div>
          </div>
        )}

        {/* ── MY BOOKINGS ── */}
        {tab === 'bookings' && (
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:10 }}>My Students &amp; Bookings</div>

            {/* Group communication notice */}
            <div style={{ background:'#fff8e8', border:'1px solid #f0d890', borderRadius:9, padding:'11px 14px', marginBottom:16, fontSize:13, color:'#7a5a00', lineHeight:1.6 }}>
              <strong>📢 Note:</strong> For all student communication, use the WhatsApp group created by Mr. Ahmed Salem. Do not contact students privately outside the group.
            </div>

            {/* Assigned students */}
            {assignedStudents.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:13.5, fontWeight:600, color:'#1a2233', marginBottom:8 }}>
                  My Assigned Students ({assignedStudents.length})
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {assignedStudents.map(sub => (
                    <div key={sub.id} style={{ background:'#f0f6ff', border:'1px solid #c0d8f8', borderRadius:8, padding:'8px 14px' }}>
                      <div style={{ fontWeight:600, fontSize:13, color:'#1a2233' }}>{sub.students?.name}</div>
                      <div style={{ fontSize:11, color:'#5a6a88' }}>{sub.subscription_type}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {bookings.length === 0 && <div style={{ color:'#9aa3b8', fontSize:13, padding:20, textAlign:'center' }}>No bookings yet.</div>}
            {bookings.map(b => (
              <div key={b.id} style={{ ...acBase.card, padding:'14px 18px', display:'flex', gap:14, flexWrap:'wrap', alignItems:'flex-start' }}>
                <div style={{ flex:1, minWidth:180 }}>
                  <div style={{ fontWeight:600, fontSize:14, color:'#1a2233', marginBottom:2 }}>{b.students?.name || 'Unknown Student'}</div>
                  {b.students?.whatsapp && (
                    <div style={{ fontSize:12, color:'#1a5fa8', fontFamily:"'JetBrains Mono',monospace", marginBottom:3 }}>
                      WA: {b.students.whatsapp}
                    </div>
                  )}
                  <div style={{ fontSize:12, color:'#5a6a88', marginBottom:4 }}>
                    {b.time_slots?.start_time ? fmtDateTime(b.time_slots.start_time) : 'Time TBC'}
                    {b.group_size > 1 && ` · ${b.group_size} students`}
                  </div>
                  {b.notes && <div style={{ fontSize:11.5, color:'#9aa3b8', fontStyle:'italic' }}>{b.notes}</div>}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' }}>
                  <span style={acBase.statusBadge(b.status)}>{b.status}</span>
                  {b.zoom_link && (
                    <a href={b.zoom_link} target="_blank" rel="noreferrer"
                      style={{ fontSize:12, color:'#1a5fa8', background:'#e8f0fb', border:'1px solid #c0d8f8', borderRadius:6, padding:'4px 10px', textDecoration:'none', fontWeight:600 }}>
                      🎥 Zoom Link
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── MY EARNINGS ── */}
        {tab === 'earnings' && (() => {
          const now          = new Date();
          const curMonth     = now.getMonth() + 1;
          const curYear      = now.getFullYear();
          const curPayroll   = payrollRecords.find(p => p.month === curMonth && p.year === curYear);
          const prevPayrolls = payrollRecords.filter(p => !(p.month === curMonth && p.year === curYear));
          const countedHrs   = hoursThisMonth.filter(h => ['taught','makeup','cancelled_by_student_charged'].includes(h.session_status)).reduce((s,h) => s + Number(h.hours_counted||0), 0);
          const taughtHrs    = hoursThisMonth.reduce((s,h) => s + Number(h.scheduled_duration||0), 0);
          const threshold    = payConfig?.threshold_hours || 0;
          const pct          = threshold > 0 ? Math.min(100, (countedHrs / threshold) * 100) : 0;
          const baseAmt      = curPayroll?.base_amount || 0;
          const bonusAmt     = curPayroll?.bonus_amount || 0;
          const deductAmt    = curPayroll?.deduction_amount || 0;
          const totalAmt     = curPayroll?.total_amount || 0;
          const ccy          = curPayroll?.currency || payConfig?.currency || 'AED';
          const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const SL = { taught:'Taught', cancelled_by_student_excused:'Excused', cancelled_by_student_charged:'Charged (½)', cancelled_by_teacher:'Cancelled', makeup:'Make-up', manual_adjustment:'Adjustment' };
          const SC = { taught:'#1a7a4a', cancelled_by_student_excused:'#9aa3b8', cancelled_by_student_charged:'#b8620a', cancelled_by_teacher:'#c0392b', makeup:'#1a5fa8', manual_adjustment:'#5a1a8a' };

          const payTypeDesc = payConfig ? (
            payConfig.payment_type === 'hourly' ? `Hourly · ${payConfig.base_rate} ${payConfig.currency}/hr` :
            payConfig.payment_type === 'fixed'  ? `Fixed · ${payConfig.fixed_amount} ${payConfig.currency}/month` :
            `${payConfig.percentage}% of ${payConfig.base_rate} ${payConfig.currency}/hr`
          ) : null;

          const statusBadge = (status) => {
            const s = { pending:{bg:'#fff8e8',c:'#b8620a',b:'#f0d890',icon:'⏳'}, approved:{bg:'#e8f0fb',c:'#1a5fa8',b:'#c0d8f8',icon:'✅'}, paid:{bg:'#e8f5ee',c:'#1a7a4a',b:'#c8ead8',icon:'💰'} }[status] || {};
            return <span style={{ fontSize:12, fontWeight:700, padding:'3px 11px', borderRadius:100, background:s.bg, color:s.c, border:`1px solid ${s.b}` }}>{s.icon} {status}</span>;
          };

          // No payment config yet
          if (!payConfig) return (
            <div style={{ ...acBase.card, textAlign:'center', padding:'36px 24px' }}>
              <div style={{ fontSize:36, marginBottom:12 }}>💰</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:8 }}>Payment Rate Not Configured</div>
              <div style={{ fontSize:13.5, color:'#5a6a88', marginBottom:18, lineHeight:1.6 }}>Your payment rate has not been set up yet. Contact Mr. Ahmed Salem to configure your payment details.</div>
              <button onClick={()=>window.open(`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent('Hello Mr. Ahmed, my payment rate has not been configured yet. Please help.')}`, '_blank')}
                style={{ background:'#25d366', color:'#fff', border:'none', padding:'12px 22px', borderRadius:9, fontSize:13.5, fontWeight:700, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:8 }}>
                <WAIcon size={15}/> Contact Mr. Ahmed Salem
              </button>
            </div>
          );

          return (
            <div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:16 }}>💰 My Earnings</div>

              {/* Current month card */}
              <div style={{ ...acBase.card, marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:14 }}>
                  <div>
                    <div style={{ fontSize:12, color:'#9aa3b8', marginBottom:4, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em', textTransform:'uppercase' }}>
                      {MONTHS_SHORT[curMonth-1]} {curYear} — Current Month
                    </div>
                    <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:700, color:'#b8860b', lineHeight:1 }}>
                      {ccy} {Number(totalAmt).toFixed(2)}
                    </div>
                    {payTypeDesc && <div style={{ fontSize:12.5, color:'#5a6a88', marginTop:5 }}>Payment type: {payTypeDesc}</div>}
                  </div>
                  <div>{curPayroll ? statusBadge(curPayroll.status) : <span style={{ fontSize:11, color:'#9aa3b8', fontStyle:'italic' }}>Not yet calculated</span>}</div>
                </div>

                {/* Breakdown grid */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:8, marginBottom: threshold>0?14:0 }}>
                  {[
                    { label:'Hours Taught',  value:`${taughtHrs.toFixed(1)}h`,  color:'#1a2233' },
                    { label:'Hours Counted', value:`${countedHrs.toFixed(1)}h`, color:'#1a5fa8' },
                    { label:'Base Amount',   value:`${ccy} ${Number(baseAmt).toFixed(2)}`,  color:'#1a2233' },
                    bonusAmt > 0 && { label:'Bonus',  value:`+${ccy} ${Number(bonusAmt).toFixed(2)}`, color:'#1a7a4a' },
                    deductAmt > 0 && { label:'Deductions', value:`-${ccy} ${Number(deductAmt).toFixed(2)}`, color:'#c0392b' },
                  ].filter(Boolean).map(item => (
                    <div key={item.label} style={{ background:'#f7f9fc', borderRadius:8, padding:'10px 12px' }}>
                      <div style={{ fontSize:10.5, color:'#9aa3b8', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em', textTransform:'uppercase', marginBottom:3 }}>{item.label}</div>
                      <div style={{ fontSize:15, fontWeight:700, color:item.color }}>{item.value}</div>
                    </div>
                  ))}
                </div>

                {/* Threshold progress */}
                {payConfig?.payment_type === 'hourly' && threshold > 0 && (
                  <div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#9aa3b8', marginBottom:4 }}>
                      <span>Bonus threshold progress</span>
                      <span>{countedHrs.toFixed(1)}h / {threshold}h</span>
                    </div>
                    <div style={{ background:'#e4ecf7', borderRadius:6, height:8, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${pct}%`, background:pct>=100?'#b8860b':'#1a5fa8', borderRadius:6, transition:'width .6s' }}/>
                    </div>
                    {pct >= 100
                      ? <div style={{ fontSize:12.5, color:'#b8860b', fontWeight:700, marginTop:5 }}>🎉 You exceeded your bonus threshold! Extra {Math.max(0,countedHrs-threshold).toFixed(1)}h × {payConfig.bonus_rate} {ccy} bonus rate</div>
                      : <div style={{ fontSize:12, color:'#9aa3b8', marginTop:4 }}>Teach {Math.max(0,threshold-countedHrs).toFixed(1)} more hours to unlock bonus rate</div>}
                  </div>
                )}

                {curPayroll?.admin_notes && (
                  <div style={{ marginTop:12, padding:'10px 13px', background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:7, fontSize:12.5, color:'#5a6a88', fontStyle:'italic' }}>
                    📝 Admin note: {curPayroll.admin_notes}
                  </div>
                )}
              </div>

              {/* Session breakdown table */}
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontWeight:700, color:'#1a2a4a', marginBottom:10 }}>Session Breakdown</div>
              {hoursThisMonth.length === 0 ? (
                <div style={{ color:'#9aa3b8', fontSize:13, marginBottom:16, textAlign:'center', padding:'20px 0' }}>No sessions logged yet this month.</div>
              ) : (
                <div style={{ ...acBase.card, padding:0, overflow:'hidden', marginBottom:16 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead>
                      <tr style={{ background:'#f7f9fc' }}>
                        {['Date','Student','Duration','Hours','Status'].map(h => (
                          <th key={h} style={{ padding:'9px 14px', textAlign:'left', fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.1em', textTransform:'uppercase', color:'#9aa3b8', borderBottom:'1px solid #dde3ed', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {hoursThisMonth.map(h => (
                        <tr key={h.id} style={{ borderBottom:'1px solid #f0f4f8' }}>
                          <td style={{ padding:'9px 14px', color:'#1a2233', whiteSpace:'nowrap' }}>{fmtDate(h.session_date)}</td>
                          <td style={{ padding:'9px 14px', color:'#1a2233' }}>{(h.students?.name||'—').split(' ')[0]}</td>
                          <td style={{ padding:'9px 14px', color:'#5a6a88' }}>{Number(h.scheduled_duration).toFixed(1)}h</td>
                          <td style={{ padding:'9px 14px', fontWeight:600, color: Number(h.hours_counted)>0?'#1a2233':'#9aa3b8' }}>{Number(h.hours_counted).toFixed(1)}h</td>
                          <td style={{ padding:'9px 14px' }}>
                            <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:100, color:SC[h.session_status]||'#5a6a88', background:`${SC[h.session_status]||'#9aa3b8'}18`, border:`1px solid ${SC[h.session_status]||'#dde3ed'}44` }}>
                              {SL[h.session_status]||h.session_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#f7f9fc', borderTop:'2px solid #dde3ed' }}>
                        <td colSpan={2} style={{ padding:'9px 14px', fontWeight:700, fontSize:13, color:'#1a2233' }}>Totals</td>
                        <td style={{ padding:'9px 14px', fontWeight:700, color:'#1a2233' }}>{taughtHrs.toFixed(1)}h</td>
                        <td style={{ padding:'9px 14px', fontWeight:700, color:'#b8860b' }}>{countedHrs.toFixed(1)}h</td>
                        <td/>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Previous months */}
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontWeight:700, color:'#1a2a4a', marginBottom:10 }}>Previous Months</div>
              {prevPayrolls.length === 0 && <div style={{ color:'#9aa3b8', fontSize:13, marginBottom:16 }}>No previous payment records yet.</div>}
              {prevPayrolls.map(p => (
                <div key={p.id} style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:10, padding:'14px 18px', marginBottom:8, display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:'#1a2233' }}>{MONTHS_SHORT[p.month-1]} {p.year}</div>
                    <div style={{ fontSize:12.5, color:'#5a6a88', marginTop:2 }}>
                      {Number(p.total_hours_taught).toFixed(1)}h taught · {Number(p.total_hours_counted).toFixed(1)}h counted
                    </div>
                  </div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a' }}>{p.currency} {Number(p.total_amount).toFixed(2)}</div>
                  {statusBadge(p.status)}
                  {p.paid_date && <div style={{ fontSize:11, color:'#9aa3b8' }}>Paid: {fmtDate(p.paid_date)}</div>}
                </div>
              ))}

              <button onClick={() => window.open(`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(`Hello Mr. Ahmed, I have a question about my payment for ${MONTHS_SHORT[curMonth-1]} ${curYear}. Please contact me when convenient.`)}`, '_blank')}
                style={{ marginTop:8, background:'#25d366', color:'#fff', border:'none', padding:'12px 22px', borderRadius:9, fontSize:13.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
                <WAIcon size={15}/> I have a question about my payment
              </button>
            </div>
          );
        })()}

        {/* ── SPEAKING EXAMS ── */}
        {tab === 'speaking' && (
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:6 }}>🎯 Speaking Mock Exams</div>
            <div style={{ fontSize:13, color:'#5a6a88', marginBottom:16, lineHeight:1.6 }}>
              Students are scheduled here by Mr. Ahmed Salem. For each exam, review the student's performance and send the report directly to their WhatsApp.
            </div>

            {speakingExams.length === 0 && (
              <div style={{ color:'#9aa3b8', fontSize:13, padding:'30px 0', textAlign:'center' }}>No speaking exams scheduled yet. Mr. Ahmed will add them here.</div>
            )}

            {speakingExams.map(exam => {
              const overall = calcOverall(exam.fluency_score, exam.lexical_score, exam.grammar_score, exam.pronunciation_score);
              const isOpen  = reportPanel === exam.id;
              const sent    = exam.status === 'report_sent';

              return (
                <div key={exam.id} style={{ background:'#fff', border:`1px solid ${sent?'#c8ead8':isOpen?'#b8860b44':'#dde3ed'}`, borderRadius:12, padding:'16px 18px', marginBottom:12 }}>
                  {/* Exam header */}
                  <div style={{ display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                    <div style={{ flex:1, minWidth:160 }}>
                      <div style={{ fontWeight:700, fontSize:14, color:'#1a2233', marginBottom:2 }}>{exam.student_name}</div>
                      <div style={{ fontSize:12, color:'#1a5fa8', fontFamily:"'JetBrains Mono',monospace", marginBottom:3 }}>WA: {exam.student_whatsapp}</div>
                      <div style={{ fontSize:12.5, color:'#5a6a88' }}>
                        {exam.scheduled_at ? fmtDateTime(exam.scheduled_at) : 'Date TBC'}
                        {' · '}
                        <span style={{ fontSize:11, fontWeight:600, color: exam.booking_type==='package'?'#1a5fa8':'#b8620a', background: exam.booking_type==='package'?'#e8f0fb':'#fff8f0', border:`1px solid ${exam.booking_type==='package'?'#c0d8f8':'#f0d890'}`, borderRadius:100, padding:'1px 7px' }}>
                          {exam.booking_type==='package'?'Package':'Visitor'}
                        </span>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:100,
                        color:  sent?'#1a7a4a':'#1a5fa8',
                        background: sent?'#e8f5ee':'#e8f0fb',
                        border: `1px solid ${sent?'#c8ead8':'#c0d8f8'}` }}>
                        {sent ? '✓ Report Sent' : '⏳ Scheduled'}
                      </span>
                      {!sent && (
                        <button onClick={()=>{ setReportPanel(isOpen?null:exam.id); setScores({fluency:'',lexical:'',grammar:'',pronunciation:''}); setReportComments(''); }}
                          style={{ background:isOpen?'#f7f9fc':'linear-gradient(135deg,#1a2a4a,#2a3a6a)', color:isOpen?'#5a6a88':'#fff', border:isOpen?'1px solid #dde3ed':'none', padding:'6px 14px', borderRadius:7, cursor:'pointer', fontSize:12.5, fontWeight:600 }}>
                          {isOpen ? 'Close' : '✍️ Write Report'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Report sent summary */}
                  {sent && exam.overall_score && (
                    <div style={{ marginTop:10, padding:'10px 14px', background:'#e8f5ee', borderRadius:8, display:'flex', gap:20, flexWrap:'wrap', fontSize:12.5, color:'#1a5a3a' }}>
                      {CRITERIA.map(c=>(
                        <span key={c.key}><strong>{c.label.split(' ')[0]}:</strong> {exam[c.key+'_score']}</span>
                      ))}
                      <span><strong>⭐ Overall:</strong> {exam.overall_score}</span>
                    </div>
                  )}

                  {/* Report writing panel */}
                  {isOpen && (
                    <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #dde3ed' }}>
                      <div style={{ fontSize:13.5, fontWeight:600, color:'#1a2a4a', marginBottom:12 }}>Examiner Report</div>

                      {/* Scores grid */}
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:10, marginBottom:14 }}>
                        {CRITERIA.map(c => (
                          <div key={c.key}>
                            <div style={{ fontSize:11, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em', textTransform:'uppercase', color:'#9aa3b8', marginBottom:5 }}>{c.label}</div>
                            <select value={scores[c.key]} onChange={e=>setScores(s=>({...s,[c.key]:e.target.value}))}
                              style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${scores[c.key]?'#b8860b':'#dde3ed'}`, borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none' }}>
                              <option value="">Select band</option>
                              {SCORE_OPTS.map(n=><option key={n} value={n}>{n}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>

                      {/* Overall band */}
                      {(() => {
                        const ov = calcOverall(scores.fluency,scores.lexical,scores.grammar,scores.pronunciation);
                        return ov !== null ? (
                          <div style={{ background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', borderRadius:9, padding:'12px 16px', marginBottom:14, display:'flex', alignItems:'center', gap:14 }}>
                            <div style={{ fontSize:10, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.14em', textTransform:'uppercase', color:'rgba(255,255,255,0.5)' }}>Overall Band</div>
                            <div style={{ fontSize:32, fontWeight:700, color:'#d4a017', fontFamily:"'Cormorant Garamond',serif", lineHeight:1 }}>⭐ {ov}</div>
                          </div>
                        ) : null;
                      })()}

                      {/* Comments */}
                      <div style={{ fontSize:11, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em', textTransform:'uppercase', color:'#9aa3b8', marginBottom:5 }}>Examiner Comments</div>
                      <textarea value={reportComments} onChange={e=>setReportComments(e.target.value)} rows={3}
                        placeholder="Detailed feedback for the student..."
                        style={{ width:'100%', padding:'10px 12px', border:'1px solid #dde3ed', borderRadius:8, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', resize:'vertical', marginBottom:14 }}/>

                      {/* Send button */}
                      <button disabled={sendingReport || !scores.fluency || !scores.lexical || !scores.grammar || !scores.pronunciation || !reportComments.trim()}
                        onClick={async () => {
                          const ov = calcOverall(scores.fluency,scores.lexical,scores.grammar,scores.pronunciation);
                          if (!ov) return;
                          setSendingReport(true);

                          const sorted = CRITERIA.map(c=>({ ...c, score:Number(scores[c.key]) })).sort((a,b)=>a.score-b.score);
                          const focusAreas = sorted.slice(0,2).map(c=>`- ${c.label}: ${c.score}`).join('\n');

                          const dateStr = exam.scheduled_at ? fmtDateTime(exam.scheduled_at) : 'Recent Session';
                          const msg = `📋 IELTS Speaking Mock Exam Report\n━━━━━━━━━━━━━━━\nStudent: ${exam.student_name}\nDate: ${dateStr}\nExaminer: ${profile.name}\n\n📊 Band Scores:\n- Fluency & Coherence: ${scores.fluency}\n- Lexical Resource: ${scores.lexical}\n- Grammatical Range & Accuracy: ${scores.grammar}\n- Pronunciation: ${scores.pronunciation}\n- ⭐ Overall Band: ${ov}\n\n💬 Examiner Feedback:\n${reportComments.trim()}\n\n🎯 Focus areas for improvement:\n${focusAreas}\n\nKeep practising — you are getting closer to your goal! 💪\n━━━━━━━━━━━━━━━\nIELTSCOMPASS\nNavigate your path to success 🧭\nwa.me/201008182425`;

                          // Save to DB
                          await supabase.from('speaking_exams').update({
                            fluency_score:       Number(scores.fluency),
                            lexical_score:       Number(scores.lexical),
                            grammar_score:       Number(scores.grammar),
                            pronunciation_score: Number(scores.pronunciation),
                            overall_score:       ov,
                            examiner_comments:   reportComments.trim(),
                            status:              'report_sent',
                            report_sent_at:      new Date().toISOString(),
                          }).eq('id', exam.id);

                          // Open WhatsApp
                          window.open(`https://wa.me/${exam.student_whatsapp.replace(/\D/g,'')}?text=${encodeURIComponent(msg)}`, '_blank');

                          setReportPanel(null);
                          setSendingReport(false);
                          fetchSpeakingExams();
                        }}
                        style={{ width:'100%', background:'#25d366', color:'#fff', border:'none', padding:'13px 0', borderRadius:9, fontSize:14, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:9, opacity:(!scores.fluency||!scores.lexical||!scores.grammar||!scores.pronunciation||!reportComments.trim())?0.5:1 }}>
                        {sendingReport ? <Spinner/> : <><WAIcon size={16}/> Send Report via WhatsApp</>}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── STUDENTS PROGRESS ── */}
        {tab === 'students-progress' && (
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:16 }}>My Students' Progress</div>
            {assignedStudents.length === 0 ? (
              <div style={{ color:'#9aa3b8', fontSize:13, padding:20 }}>No students assigned yet.</div>
            ) : (
              <div>
                {assignedStudents.map(sub => (
                  <div key={sub.id} style={{ ...acBase.card, marginBottom:16 }}>
                    <div style={{ fontWeight:700, fontSize:15, color:'#1a2a4a', marginBottom:12 }}>{sub.students?.name || 'Student'}</div>
                    <ProgressDashboard studentId={sub.students?.id || sub.student_id}/>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── MY PROFILE ── */}
        {tab === 'profile' && (
          <div style={{ maxWidth:560 }}>
            <div style={acBase.card}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:16 }}>Edit My Profile</div>

              {/* Photo preview */}
              <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:18 }}>
                {editPhoto ? (
                  <img src={editPhoto} alt="profile" style={{ width:64, height:64, borderRadius:'50%', objectFit:'cover', border:'2px solid #e8edf5' }} onError={e=>{ e.target.style.display='none'; }}/>
                ) : (
                  <div style={{ width:64, height:64, borderRadius:'50%', background:'#e4ecf7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:700, color:'#1a2a4a', flexShrink:0 }}>
                    {editName.trim().charAt(0).toUpperCase() || '?'}
                  </div>
                )}
                <div style={{ flex:1 }}>
                  <label style={acBase.lbl}>Profile Photo URL</label>
                  <input value={editPhoto} onChange={e=>setEditPhoto(e.target.value)} style={{ ...acBase.inp, marginBottom:4 }} placeholder="Paste Google Drive or direct image URL"/>
                  <div style={{ fontSize:11, color:'#9aa3b8' }}>Paste a direct image link or Google Drive share URL</div>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={acBase.lbl}>Full Name</label>
                  <input value={editName} onChange={e=>setEditName(e.target.value)} style={acBase.inp}/>
                </div>
                <div>
                  <label style={acBase.lbl}>WhatsApp Number</label>
                  <input value={editWA} onChange={e=>setEditWA(e.target.value)} style={acBase.inp} placeholder="201012345678"/>
                </div>
                <div>
                  <label style={acBase.lbl}>Nationality</label>
                  <input value={editNat} onChange={e=>setEditNat(e.target.value)} style={acBase.inp} placeholder="e.g. Egyptian, British"/>
                </div>
                <div>
                  <label style={acBase.lbl}>Years of Experience</label>
                  <input type="number" min={0} value={editYears} onChange={e=>setEditYears(e.target.value)} style={acBase.inp}/>
                </div>
              </div>

              <label style={acBase.lbl}>Short Bio</label>
              <textarea value={editBio} onChange={e=>setEditBio(e.target.value)} rows={3}
                placeholder="Tell students about your teaching style and experience..."
                style={{ ...acBase.inp, resize:'vertical', marginBottom:16 }}/>

              {/* Voice intro */}
              <label style={acBase.lbl}>Voice Introduction URL</label>
              <input value={editVoice} onChange={e=>setEditVoice(e.target.value)} style={{ ...acBase.inp, marginBottom:4 }} placeholder="Paste audio link (Google Drive, SoundCloud, etc.)"/>
              <div style={{ fontSize:11, color:'#9aa3b8', marginBottom:14 }}>Record a 30-second introduction and paste the link here</div>

              {/* Sample work */}
              <div style={{ marginBottom:16 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                  <div style={{ ...acBase.lbl, marginBottom:0 }}>Sample Work / Portfolio ({editSamples.length}/5)</div>
                  {editSamples.length < 5 && (
                    <button onClick={()=>setEditSamples(s=>[...s,{title:'',url:'',type:'Recording'}])}
                      style={{ fontSize:11.5, color:'#b8860b', border:'1px solid #b8860b44', background:'#b8860b11', borderRadius:6, padding:'3px 10px', cursor:'pointer' }}>
                      + Add Sample
                    </button>
                  )}
                </div>
                {editSamples.map((sm,i)=>(
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto auto', gap:6, marginBottom:6, alignItems:'center' }}>
                    <input value={sm.title} onChange={e=>setEditSamples(s=>s.map((x,j)=>j===i?{...x,title:e.target.value}:x))}
                      placeholder="Sample title" style={{ ...acBase.inp, marginBottom:0, fontSize:12 }}/>
                    <input value={sm.url} onChange={e=>setEditSamples(s=>s.map((x,j)=>j===i?{...x,url:e.target.value}:x))}
                      placeholder="URL" style={{ ...acBase.inp, marginBottom:0, fontSize:12 }}/>
                    <select value={sm.type} onChange={e=>setEditSamples(s=>s.map((x,j)=>j===i?{...x,type:e.target.value}:x))}
                      style={{ ...acBase.inp, marginBottom:0, fontSize:12, width:110 }}>
                      {['Recording','Presentation','Worksheet','Other'].map(v=><option key={v}>{v}</option>)}
                    </select>
                    <button onClick={()=>setEditSamples(s=>s.filter((_,j)=>j!==i))}
                      style={{ background:'rgba(192,57,43,0.1)', color:'#c0392b', border:'none', borderRadius:6, padding:'6px 9px', cursor:'pointer' }}>✕</button>
                  </div>
                ))}
                {editSamples.length===0 && <div style={{ fontSize:12, color:'#9aa3b8', fontStyle:'italic' }}>No samples added. Click "+ Add Sample" to add portfolio links.</div>}
              </div>

              {/* Zoom (read-only) */}
              <div style={{ background:'#f7f9fc', border:'1px solid #e8edf5', borderRadius:8, padding:'11px 14px', marginBottom:16 }}>
                <div style={{ ...acBase.lbl, marginBottom:4 }}>Zoom Meeting ID (set by admin)</div>
                <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:13, color:'#1a2233' }}>{profile.zoom_meeting_id || 'Not assigned yet'}</div>
              </div>

              {/* Change Password */}
              <div style={{ borderTop:'1px solid #e8edf5', paddingTop:14, marginTop:4, marginBottom:14 }}>
                <div style={{ fontSize:13.5, fontWeight:600, color:'#1a2233', marginBottom:4 }}>Change Password</div>
                <div style={{ fontSize:12, color:'#9aa3b8', marginBottom:10 }}>Leave blank to keep your current password. Min. 8 characters.</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <label style={acBase.lbl}>New Password</label>
                    <input type="password" value={editNewPwd} onChange={e=>setEditNewPwd(e.target.value)} placeholder="Leave blank to keep" style={acBase.inp}/>
                  </div>
                  <div>
                    <label style={acBase.lbl}>Confirm Password</label>
                    <input type="password" value={confNewPwd} onChange={e=>setConfNewPwd(e.target.value)} style={acBase.inp}/>
                  </div>
                </div>
              </div>

              {saveMsg && (
                <div style={{ padding:'10px 13px', borderRadius:8, marginBottom:12, fontSize:13, fontWeight:500,
                  background: saveMsg.startsWith('✓') ? '#e8f5ee' : '#fff5f5',
                  border: `1px solid ${saveMsg.startsWith('✓') ? '#c8ead8' : 'rgba(192,57,43,0.2)'}`,
                  color: saveMsg.startsWith('✓') ? '#1a7a4a' : '#c0392b' }}>
                  {saveMsg}
                </div>
              )}

              <button onClick={saveProfile} disabled={saving}
                style={{ ...acBase.btn, opacity:saving?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                {saving ? <Spinner/> : 'Save Profile'}
              </button>
            </div>

            {/* Earnings note if no config */}
            {tab === 'profile' && !saving && (
              <div style={{ ...acBase.card, marginTop:12, background:'#f8f9fb', border:'1px solid #e8edf5' }}>
                <div style={{ fontSize:13, color:'#5a6a88', marginBottom:8 }}>Questions about your schedule or pay?</div>
                <button onClick={()=>window.open(`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent('Hello Mr. Ahmed, I have a question about my teaching account.')}`, '_blank')}
                  style={{ background:'#25d366', color:'#fff', border:'none', padding:'9px 18px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700, display:'flex', alignItems:'center', gap:7 }}>
                  <WAIcon size={14}/> Contact Mr. Ahmed
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── SLOT MODAL ── */}
      {slotModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div style={{ ...acBase.card, maxWidth:380, width:'100%', margin:0, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>
              {slotModal.slot ? 'Edit Slot' : 'Add Time Slot'}
            </div>
            <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:'#9aa3b8', marginBottom:16 }}>{slotModal.date}</div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div>
                <label style={acBase.lbl}>Start Time</label>
                <input type="time" value={slotStart} onChange={e=>setSlotStart(e.target.value)} style={{ ...acBase.inp, marginBottom:0 }}/>
              </div>
              <div>
                <label style={acBase.lbl}>End Time</label>
                <input type="time" value={slotEnd} onChange={e=>setSlotEnd(e.target.value)} style={{ ...acBase.inp, marginBottom:0 }}/>
              </div>
            </div>
            <div style={{ height:14 }}/>

            <label style={acBase.lbl}>Max Students</label>
            <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
              {[1,2,3,4,8,10].map(n=>(
                <button key={n} onClick={()=>setSlotMax(n)} style={{ padding:'6px 12px', borderRadius:7, cursor:'pointer', border:`1.5px solid ${slotMax===n?'#b8860b':'#dde3ed'}`, background:slotMax===n?'#fffaf0':'#f7f9fc', color:slotMax===n?'#b8860b':'#5a6a88', fontSize:12.5, fontWeight:slotMax===n?700:400 }}>{n}</button>
              ))}
            </div>

            <label style={acBase.lbl}>Slot Type</label>
            <div style={{ display:'flex', gap:6, marginBottom:14 }}>
              {SLOT_TYPES.map(t=>(
                <button key={t} onClick={()=>setSlotType(t)} style={{ flex:1, padding:'7px', borderRadius:7, cursor:'pointer', border:`1.5px solid ${slotType===t?slotColor[t]:'#dde3ed'}`, background:slotType===t?`${slotColor[t]}15`:'#f7f9fc', color:slotType===t?slotColor[t]:'#5a6a88', fontSize:12, fontWeight:slotType===t?700:400 }}>
                  {t.charAt(0).toUpperCase()+t.slice(1)}
                </button>
              ))}
            </div>

            {!slotModal.slot && (
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'#1a2233' }}>
                  <input type="checkbox" checked={recurring} onChange={e=>setRecurring(e.target.checked)}/>
                  Repeat weekly (recurring slot)
                </label>
                {recurring && (
                  <div style={{ marginTop:10 }}>
                    <label style={acBase.lbl}>Repeat for how many weeks?</label>
                    <input type="number" min={1} max={12} value={recurWeeks} onChange={e=>setRecurWeeks(Number(e.target.value))}
                      style={{ ...acBase.inp, marginBottom:0, width:80 }}/>
                  </div>
                )}
              </div>
            )}

            {slotErr && <div style={acBase.err}>{slotErr}</div>}

            <div style={{ display:'flex', gap:8 }}>
              <button onClick={saveSlot} disabled={slotLoading}
                style={{ ...acBase.btn, flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:slotLoading?0.7:1 }}>
                {slotLoading ? <Spinner/> : (slotModal.slot ? 'Update' : `Add Slot${recurring?' (×'+recurWeeks+')':''}`)}
              </button>
              {slotModal.slot && (
                <button onClick={()=>deleteSlot(slotModal.slot.id)}
                  style={{ background:'#fff5f5', border:'1.5px solid rgba(192,57,43,0.3)', color:'#c0392b', padding:'0 14px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600 }}>
                  Delete
                </button>
              )}
              <button onClick={()=>setSlotModal(null)}
                style={{ background:'#f7f9fc', border:'1.5px solid #dde3ed', color:'#5a6a88', padding:'0 14px', borderRadius:8, cursor:'pointer', fontSize:13 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
